import React, { useEffect, useState, useCallback } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import { Typography, Box } from '@material-ui/core';
import { IconButton, Tooltip } from '@mui/material';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import moment from 'moment';

import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import TableOmniNew from 'allGlobalComponents/TableOmniNew';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import MediaModal from 'allGlobalComponents/MediaModal';
import AudioPlayerModal from 'app/main/globalComponents/AudioPlayerModal';
import { formatDate } from 'app/utility/ExportToExcelUtility';
import { useSnackbar, useApi, useExport, usePagination } from 'app/hooks';

const useStyles = makeStyles(theme => ({
	content: {
		width: '100%',
		padding: '0 16px',
		marginTop: '10px'
	},
	headerWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		padding: theme.spacing(0, 2)
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center'
	},
	headerIcon: {
		marginRight: theme.spacing(1.5)
	},
	headerTitle: {
		fontSize: '18px',
		fontWeight: 600,
		color: theme.palette.text.primary
	},
	viewLink: {
		maxWidth: '200px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		textDecoration: 'underline',
		cursor: 'pointer'
	},
	playButton: {
		backgroundColor: '#1976d2 !important',
		color: 'white !important',
		width: '40px !important',
		height: '40px !important'
	}
}));

const CALL_TYPE_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'incoming', label: 'Incoming' },
	{ value: 'outgoing', label: 'Outgoing' }
];

// Helpers
const capitalizeWords = (str) => {
	if (!str || typeof str !== 'string') return '-';
	return str
		.split(' ')
		.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ');
};

const formatDuration = (sec) => {
	if (!sec || sec < 1) return '0s';
	const total = parseInt(sec, 10);
	if (total < 60) return `${total}s`;
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	let result = '';
	if (h > 0) result += `${h} h `;
	if (m > 0) result += `${m} min `;
	if (s > 0) result += `${s}s`;
	return result.trim();
};

// Recording url can arrive as null, an empty string or a literal "null" from the API
const getRecordingUrl = (value) => {
	if (!value || typeof value !== 'string') return '';
	const url = value.trim();
	if (!url || url === 'null' || url === 'undefined') return '';
	return url;
};

// Permission helpers
const getAgentPermissions = () => {
	try {
		const stored = localStorage.getItem('user_acl');
		return stored ? JSON.parse(stored) : {};
	} catch {
		return {};
	}
};

const getBackAcl = () => {
	try {
		return JSON.parse(localStorage.getItem('back_acl')) || {};
	} catch {
		return {};
	}
};

const getClientId = () => {
	try {
		return (JSON.parse(localStorage.getItem('user_data')) || {}).client_id || '';
	} catch {
		return '';
	}
};

function CallHistory() {
	const classes = useStyles();
	const theme = useTheme();

	// Permissions
	const agentPermissions = getAgentPermissions();
	const back_ACL = getBackAcl();
	const canViewCalls = back_ACL['BACK:whatsapp_calls'] == 1;
	const canViewRecording = agentPermissions['FRONT:/channels/whatsapp-call/recording'] == 1;

	// Custom Hooks
	const { snackbar, showError, showWarning } = useSnackbar();
	const { isLoading, request } = useApi();
	const { request: requestClientDetails } = useApi();
	const { isExporting, handleExport } = useExport();
	const { currentParams, setCurrentParams, totalItems, setTotalItems, resetPage } = usePagination(10);

	// Local State
	const [data, setData] = useState([]);
	// Meta recording column only applies when the client has call recording turned on
	const [isMetaRecordingEnabled, setIsMetaRecordingEnabled] = useState(false);
	const [startDate, setStartDate] = useState(moment.utc().startOf('day'));
	const [endDate, setEndDate] = useState(moment.utc().endOf('day'));

	const [filterValues, setFilterValues] = useState({
		call_type: 'all'
	});

	// Media / Audio modals
	const [mediaModal, setMediaModal] = useState(false);
	const [mediaForModal, setMediaForModal] = useState('');
	const [mediaTypeForModal, setMediaTypeForModal] = useState('');
	const [mediaTitleForModal, setMediaTitleForModal] = useState('');
	const [isLoadingDoc, setIsLoadingDoc] = useState(false);

	const [audioPlayerOpen, setAudioPlayerOpen] = useState(false);
	const [selectedRecording, setSelectedRecording] = useState({ url: '', callId: '' });

	// Build payload
	const buildPayload = useCallback(() => {
		const payload = {
			page: currentParams.page,
			limit: currentParams.limit,
			start_date: startDate,
			end_date: endDate
		};
		if (filterValues.call_type && filterValues.call_type !== 'all') {
			payload.call_type = filterValues.call_type;
		}
		return payload;
	}, [currentParams, filterValues, startDate, endDate]);

	// Recording settings live on the client record; statuses come back as strings
	useEffect(() => {
		const clientId = getClientId();
		if (!clientId) return;

		requestClientDetails({
			module: 'BusinessDetails',
			method: 'get',
			payload: { key: ':client_id', value: clientId, params: {} },
			onSuccess: (response) => {
				const client = response?.data?.data?.clients?.[0] || {};
				setIsMetaRecordingEnabled(String(client.recording_status) === 'true');
			},
			onError: () => setIsMetaRecordingEnabled(false)
		});
	}, []);

	// Fetch data
	useEffect(() => {
		if (!canViewCalls) return;

		request({
			module: 'reports',
			method: 'callHistory',
			payload: buildPayload(),
			onSuccess: (response) => {
				const res = response.data.data.whatsappCalls;
				setData(res.whatsappCalls || []);
				setTotalItems(res.totalItems || 0);
			},
			onError: (message) => showError(message || 'Error fetching call history')
		});
	}, [currentParams, filterValues, startDate, endDate]);

	// Handlers
	const handleDateChange = ({ startDate: s, endDate: e }) => {
		if (s && e) {
			setStartDate(s);
			setEndDate(e);
			resetPage();
		}
	};

	const handleFiltersApply = (filters) => {
		setFilterValues(filters);
		resetPage();
	};

	const handleFiltersClear = (filters) => {
		setFilterValues(filters);
		resetPage();
	};

	const handleRefresh = () => {
		setCurrentParams(prev => ({ ...prev }));
	};

	const handleViewMedia = (value, type, title = '') => {
		setMediaForModal(value);
		setMediaTypeForModal(type);
		setMediaTitleForModal(title);
		setMediaModal(true);
	};

	const handleOpenAudioPlayer = (url, callId) => {
		setSelectedRecording({ url, callId });
		setAudioPlayerOpen(true);
	};

	const handleDownloadRecording = async (url, callId) => {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			const downloadUrl = URL.createObjectURL(blob);
			const filename = `call_recording_${callId || 'unknown'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.webm`;
			const a = document.createElement('a');
			a.href = downloadUrl;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			showError('Failed to download recording');
		}
	};

	// Extract media URL/filename from attachments
	const extractMedia = (attachments, fallbackName = 'Unknown') => {
		if (!attachments || !attachments[0]) return { url: '', filename: fallbackName };
		let url = '';
		let filename = fallbackName;
		if (attachments[0].attribute_name === 'filename') {
			url = attachments[1]?.attribute_value || '';
			filename = attachments[0]?.attribute_value || fallbackName;
		} else if (attachments[1]?.attribute_name === 'filename') {
			url = attachments[0]?.attribute_value || '';
			filename = attachments[1]?.attribute_value || fallbackName;
		}
		return { url, filename };
	};

	// Export columns
	const exportColumns = [
		{ header: 'S.No', key: '_index' },
		{ header: 'Call ID', key: 'call_id', transform: (v) => v || '-' },
		{ header: 'Name', key: 'name', transform: (v) => v || '-' },
		{ header: 'Number', key: 'number', transform: (v) => v ? `'${v}` : '-' },
		{ header: 'Call Type', key: 'call_type', transform: (v) => capitalizeWords(v) },
		{ header: 'Start Time', key: 'call_start_time', transform: (v) => v ? moment.unix(v).format('MMM Do YYYY, h:mm A') : '-' },
		{ header: 'End Time', key: 'call_end_time', transform: (v) => v ? moment.unix(v).format('MMM Do YYYY, h:mm A') : '-' },
		{ header: 'Duration', key: 'call_duration', transform: (v) => formatDuration(v) },
		{ header: 'Accepted By', key: 'username', transform: (v) => capitalizeWords(v) },
		{ header: 'Status', key: 'call_status', transform: (v) => capitalizeWords(v) },
		{ header: 'Call Blocks', key: 'blocks', transform: (v) => v || '-' },
		{ header: 'Date Time', key: 'dt', transform: (v) => formatDate(v) },
		{ header: 'Call Journey', key: 'interactions', transform: (v) => Array.isArray(v) && v.length ? v.map(i => i.event_type).filter(Boolean).join(', ') : '-' },
		...(canViewRecording
			? [{ header: 'Recording URL', key: 'recording_url', transform: (v) => getRecordingUrl(v) || '-' }]
			: []),
		...(isMetaRecordingEnabled
			? [{ header: 'Meta Recording URL', key: 'meta_call_recording', transform: (v) => getRecordingUrl(v) || '-' }]
			: [])
	];

	// Export handler
	const onExport = () => {
		if (!canViewCalls) {
			showWarning('You do not have permission to export');
			return;
		}

		const exportPayload = { ...buildPayload(), page: 0, limit: totalItems || 999999 };

		handleExport({
			module: 'reports',
			method: 'callHistory',
			payload: exportPayload,
			fileNamePrefix: 'call_history',
			sheetName: 'Call History',
			getDataFromResponse: (res) => res.data.data.whatsappCalls.whatsappCalls || [],
			columns: exportColumns,
			onError: (msg) => showWarning(msg || 'No data to export')
		});
	};

	// Filter fields
	const filterFields = [
		{
			name: 'call_type',
			label: 'Call Type',
			type: 'select',
			options: CALL_TYPE_OPTIONS,
			defaultValue: 'all'
		}
	];

	// Pill mappings
	const callTypePillMapping = {
		incoming: { label: 'Incoming', color: '#0369a1', bgColor: '#e0f2fe' },
		outgoing: { label: 'Outgoing', color: '#7c2d12', bgColor: '#ffedd5' }
	};

	const statusPillMapping = {
		accepted: { label: 'Accepted', color: '#166534', bgColor: '#dcfce7' },
		completed: { label: 'Completed', color: '#166534', bgColor: '#dcfce7' },
		missed: { label: 'Missed', color: '#991b1b', bgColor: '#fee2e2' },
		rejected: { label: 'Rejected', color: '#991b1b', bgColor: '#fee2e2' },
		failed: { label: 'Failed', color: '#991b1b', bgColor: '#fee2e2' }
	};

	// Message cell renderer
	const renderMessageCell = (row) => {
		if (!row.message || Object.keys(row.message).length === 0) return '-';

		const { message_type, message_body, attachments } = row.message;

		const ViewLink = ({ onClick, tooltip }) => (
			<Tooltip title={tooltip} arrow>
				<div
					onClick={onClick}
					className={classes.viewLink}
					style={{ color: theme.palette.secondary.value }}
				>
					View
				</div>
			</Tooltip>
		);

		switch (message_type) {
			case 'text': {
				if (!message_body) return 'No text content';
				const truncated = message_body.length > 50 ? `${message_body.substring(0, 50)}...` : message_body;
				return <ViewLink tooltip={`View text: ${truncated}`} onClick={() => handleViewMedia(message_body, 'TEXT')} />;
			}
			case 'image': {
				const { url, filename } = extractMedia(attachments, 'Unknown image');
				if (!url) return 'No image available';
				return <ViewLink tooltip={`View image: ${filename}`} onClick={() => handleViewMedia(url, 'IMAGE')} />;
			}
			case 'video': {
				const { url, filename } = extractMedia(attachments, 'Unknown video');
				if (!url) return 'No video available';
				return <ViewLink tooltip={`View video: ${filename}`} onClick={() => handleViewMedia(url, 'VIDEO')} />;
			}
			case 'document': {
				const { url, filename } = extractMedia(attachments, 'Unknown document');
				if (!url) return 'No document available';
				return <ViewLink tooltip={`View document: ${filename}`} onClick={() => handleViewMedia(url, 'DOCUMENT')} />;
			}
			case 'interactive': {
				if (!row.message.interactive) return 'Interactive message';
				return <ViewLink tooltip="View interactive message" onClick={() => handleViewMedia(row.message, 'INTERACTIVE')} />;
			}
			default:
				return message_body || 'Unknown message type';
		}
	};

	// Interactions cell renderer
	const renderInteractionsCell = (row) => {
		const interactions = Array.isArray(row.interactions) ? row.interactions : [];
		if (interactions.length === 0) return '-';

		return (
			<Tooltip title={`View call journey (${interactions.length} event${interactions.length > 1 ? 's' : ''})`} arrow>
				<div
					onClick={() => handleViewMedia(interactions, 'INTERACTIONS', 'Call Journey')}
					className={classes.viewLink}
					style={{ color: theme.palette.secondary.value }}
				>
					{`View (${interactions.length})`}
				</div>
			</Tooltip>
		);
	};

	// Recording cell renderer (url may be null / empty for a given call)
	const renderRecordingCell = (value, callId, tooltip) => {
		const url = getRecordingUrl(value);
		if (!url) return '-';

		return (
			<Tooltip title={tooltip} arrow>
				<IconButton
					onClick={(e) => {
						e.stopPropagation();
						handleOpenAudioPlayer(url, callId);
					}}
					className={classes.playButton}
				>
					<PlayArrowIcon style={{ fontSize: '24px' }} />
				</IconButton>
			</Tooltip>
		);
	};

	// Table columns
	const headCells = [
		{
			id: 'call_id',
			numeric: false,
			disablePadding: false,
			label: 'Call ID',
			msg: true,
			truncateLength: 10,
			customRow: row => row.call_id || '-'
		},
		{
			id: 'name',
			numeric: false,
			disablePadding: false,
			label: 'Name',
			tooltip: true,
			customRow: row => row.name || '-'
		},
		{
			id: 'number',
			numeric: false,
			disablePadding: false,
			label: 'Number',
			tooltip: true,
			customRow: row => row.number || '-'
		},
		{
			id: 'call_type',
			numeric: false,
			disablePadding: false,
			label: 'Call Type',
			pill: true,
			pillConfig: { mapping: callTypePillMapping }
		},
		{
			id: 'call_start_time',
			numeric: false,
			disablePadding: false,
			label: 'Start Time',
			customRow: row => row.call_start_time ? moment.unix(row.call_start_time).format('h:mm A') : '-'
		},
		{
			id: 'call_end_time',
			numeric: false,
			disablePadding: false,
			label: 'End Time',
			customRow: row => row.call_end_time ? moment.unix(row.call_end_time).format('h:mm A') : '-'
		},
		{
			id: 'call_duration',
			numeric: false,
			disablePadding: false,
			label: 'Duration',
			customRow: row => formatDuration(row.call_duration)
		},
		{
			id: 'username',
			numeric: false,
			disablePadding: false,
			label: 'Accepted By',
			customRow: row => capitalizeWords(row.username)
		},
		{
			id: 'call_status',
			numeric: false,
			disablePadding: false,
			label: 'Status',
			pill: true,
			pillConfig: {
				mapping: (value) => statusPillMapping[value?.toLowerCase()] || { label: capitalizeWords(value), color: theme.palette.text.primary, bgColor: theme.palette.action.hover }
			}
		},
		{
			id: 'blocks',
			numeric: false,
			disablePadding: false,
			label: 'Call Blocks',
			customRow: row => row.blocks || '-'
		},
		{
			id: 'dt',
			numeric: false,
			disablePadding: false,
			label: 'Date Time',
			dt: true
		},
		{
			id: 'message',
			numeric: false,
			disablePadding: false,
			label: 'Message',
			customRow: renderMessageCell
		},
		{
			id: 'interactions',
			numeric: false,
			disablePadding: false,
			label: 'Call Journey',
			customRow: renderInteractionsCell
		},
		// Recording column (permission-gated)
		...(canViewRecording
			? [
				{
					id: 'recording_url',
					numeric: true,
					disablePadding: false,
					label: 'Recording',
					customRow: row => renderRecordingCell(row.recording_url, row.call_id, 'Play Recording')
				}
			]
			: []),
		// Meta recording column (driven only by recording_status on the client record)
		...(isMetaRecordingEnabled
			? [
				{
					id: 'meta_call_recording',
					numeric: true,
					disablePadding: false,
					label: 'Meta Recording',
					customRow: row => renderRecordingCell(row.meta_call_recording, row.call_id, 'Play Meta Recording')
				}
			]
			: [])
	];

	return (
		<>
			{/* Media Modal */}
			{mediaModal && (
				<MediaModal
					mediaModal={mediaModal}
					mediaTypeForModal={mediaTypeForModal}
					mediaForModal={mediaForModal}
					title={mediaTitleForModal}
					setMediaModal={setMediaModal}
					setIsLoadingDoc={setIsLoadingDoc}
					isLoadingDoc={isLoadingDoc}
				/>
			)}

			{/* Audio Player */}
			<AudioPlayerModal
				open={audioPlayerOpen}
				onClose={() => setAudioPlayerOpen(false)}
				audioUrl={selectedRecording.url}
				callId={selectedRecording.callId}
				onDownload={handleDownloadRecording}
			/>

			<FusePageSimple
				header={
					<div className={classes.headerWrapper}>
						<div className={classes.headerLeft}>
							<FuseAnimate animation="transition.expandIn" delay={300}>
								<AccessTimeIcon fontSize="large" className={classes.headerIcon} />
							</FuseAnimate>
							<FuseAnimate animation="transition.slideLeftIn" delay={300}>
								<Typography variant="h6" className={classes.headerTitle}>
									Call History
								</Typography>
							</FuseAnimate>
						</div>
					</div>
				}
				content={
					<div className={classes.content}>
						<DateFilterWithExport
							startDate={startDate}
							endDate={endDate}
							onDateChange={handleDateChange}
							showDateRange={true}
							showExport={canViewCalls}
							onExport={onExport}
							isExporting={isExporting}
							filterFields={filterFields}
							filterValues={filterValues}
							onFiltersApply={handleFiltersApply}
							onFiltersClear={handleFiltersClear}
							showRefresh={true}
							onRefresh={handleRefresh}
						/>

						<TableOmniNew
							rows={data}
							headCells={headCells}
							perPageRows={currentParams.limit}
							totalItems={totalItems}
							curPage={currentParams.page}
							setCurrentParams={setCurrentParams}
							loading={isLoading}
							isLoadingPagination={false}
							fixedHeight={true}
							maxTableHeight="600px"
							rowsPerPageOptions={[5, 10, 25, 50, 100]}
							emptyMessage="No Calls Found"
						/>

						<SnackbarOmni {...snackbar} />
					</div>
				}
			/>
		</>
	);
}

export default React.memo(CallHistory);